import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  findingsAdminApi,
  isDeployJobActive,
  workerApi,
  type CopilotFindingDto,
} from '../../api/findings';
import { formatApiError } from '../../utils/errors';

/**
 * Admin triage board for the copilot findings registry (/admin/findings).
 * One screen, two planes: the cross-workspace ticket table (priority ▲▼,
 * reject, markdown export) and the dev worker plane (heartbeat chip, Deploy
 * dabar + last job output). 20 s refetch keeps both live without sockets.
 */

const CATEGORY_BADGE: Record<string, string> = {
  ClientPattern: 'bg-amber-100 text-amber-700',
  ProductBug: 'bg-red-100 text-red-700',
};

/**
 * MySQL DATETIME values come back with Kind=Unspecified — serialized WITHOUT
 * the Z suffix — and JS would parse them as LOCAL time, shifting ages by the
 * viewer's timezone (the EEST +3h „3 valandų senumo" bug on a brand-new
 * finding). Anything without an explicit offset is UTC by contract.
 */
function parseAsUtc(iso: string): Date {
  return new Date(/[zZ]$|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`);
}

/** Age of a finding: whole days, hours under a day (triage scale, not minutes). */
function ageLabel(iso: string, t: (key: string, opts?: Record<string, unknown>) => string): string {
  const ms = Date.now() - parseAsUtc(iso).getTime();
  const days = Math.floor(ms / 86_400_000);
  if (days >= 1) return t('findings.ageDays', { days });
  return t('findings.ageHours', { hours: Math.max(0, Math.floor(ms / 3_600_000)) });
}

export default function FindingsBoard() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  // Status + list on the same 20 s heartbeat — the board is a watchtower.
  const { data: status, isLoading: statusLoading } = useQuery({
    queryKey: ['worker-status'],
    queryFn: workerApi.status,
    refetchInterval: 20_000,
    retry: false,
  });
  const { data: findings, isLoading } = useQuery({
    queryKey: ['admin-findings'],
    queryFn: () => findingsAdminApi.list(),
    refetchInterval: 20_000,
  });

  const priorityMutation = useMutation({
    mutationFn: ({ id, priority }: { id: number; priority: number }) =>
      findingsAdminApi.setPriority(id, priority),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['admin-findings'] });
    },
    onError: (err: Error) => setError(formatApiError(err) || t('findings.actionFailed')),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, next }: { id: number; next: string }) =>
      findingsAdminApi.setStatus(id, next),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['admin-findings'] });
    },
    onError: (err: Error) => setError(formatApiError(err) || t('findings.actionFailed')),
  });

  const deployMutation = useMutation({
    mutationFn: () => workerApi.createDeployJob(),
    onSuccess: async () => {
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['worker-status'] });
    },
    onError: (err: Error) => setError(formatApiError(err) || t('findings.actionFailed')),
  });

  const heartbeat = status?.heartbeat;
  const secondsSince = heartbeat?.lastSeenUtc
    ? Math.max(0, Math.floor((Date.now() - new Date(heartbeat.lastSeenUtc).getTime()) / 1000))
    : null;
  const workerOnline = !!heartbeat?.connected && secondsSince !== null;

  const deployJobs = status?.deployJobs ?? [];
  const deployActive = deployJobs.some(isDeployJobActive);
  const latestJob = deployJobs[0] ?? null;

  const exportFinding = (id: number) => {
    findingsAdminApi.export(id).catch(err => setError(err instanceof Error ? err.message : t('findings.actionFailed')));
  };

  const statusLabel = (s: string) => {
    const known = ['Registered', 'Queued', 'PickedUp', 'InProgress', 'Fixed', 'Done', 'Rejected'];
    return known.includes(s) ? t(`findings.status.${s}`) : s;
  };
  const catLabel = (c: string) =>
    c === 'ClientPattern' || c === 'ProductBug' ? t(`findings.cat.${c}`) : c;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between py-2 border-b border-gray-100">
        <h1 className="text-xl font-semibold text-gray-900">{t('findings.title')}</h1>
        {/* Worker chip — connection recency from the heartbeat */}
        <span
          className={`inline-flex items-center gap-1.5 px-2 py-1 text-xs rounded-full ${
            workerOnline ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
          }`}
        >
          <span className={`w-1.5 h-1.5 rounded-full ${workerOnline ? 'bg-green-500' : 'bg-gray-400'}`} />
          {statusLoading
            ? t('common.loading')
            : workerOnline
              ? t('findings.worker.online', { seconds: secondsSince })
              : t('findings.worker.offline')}
        </span>
      </div>

      {/* Dev plane: Deploy dabar + the last job. One queued/running job at a time. */}
      <div className="flex items-center gap-3 flex-wrap">
        <button
          onClick={() => deployMutation.mutate()}
          disabled={deployActive || deployMutation.isPending}
          className="px-3 py-1.5 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-40"
        >
          {t('findings.deploy.now')}
        </button>
        {latestJob && (
          <span className="flex items-center gap-2 text-xs text-gray-500 min-w-0">
            <span className="font-medium text-gray-700">{t('findings.deploy.latest')} #{latestJob.id}</span>
            <span className={isDeployJobActive(latestJob) ? 'text-amber-600 font-medium' : ''}>
              {latestJob.status}
            </span>
            {latestJob.outputTail && (
              <code
                className="truncate max-w-[420px] font-mono text-[11px] text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded"
                title={latestJob.outputTail}
              >
                {latestJob.outputTail.split('\n').pop()?.trim()}
              </code>
            )}
          </span>
        )}
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400 py-4">{t('common.loading')}</p>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase">
                <th className="px-4 py-2 font-semibold">{t('findings.col.title')}</th>
                <th className="px-4 py-2 font-semibold w-36">{t('findings.col.category')}</th>
                <th className="px-4 py-2 font-semibold w-40">{t('findings.col.workspace')}</th>
                <th className="px-4 py-2 font-semibold w-32">{t('findings.col.status')}</th>
                <th className="px-4 py-2 font-semibold w-36">{t('findings.col.priority')}</th>
                <th className="px-4 py-2 font-semibold w-24">{t('findings.col.age')}</th>
                <th className="px-4 py-2 font-semibold w-48">{t('findings.col.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {(findings ?? []).map((f: CopilotFindingDto) => {
                const terminal = f.status === 'Rejected' || f.status === 'Done';
                return (
                  <tr key={f.id} className="border-t border-gray-50 align-top">
                    <td className="px-4 py-2.5">
                      <span className="font-medium text-gray-900" title={f.description}>{f.title}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`px-2 py-0.5 text-[11px] font-semibold rounded-full ${CATEGORY_BADGE[f.category] ?? 'bg-gray-100 text-gray-600'}`}>
                        {catLabel(f.category)}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-gray-500 truncate max-w-[160px]">
                      {f.workspaceName ?? '—'}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600">
                      {statusLabel(f.status)}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-1.5">
                        <span className="tabular-nums text-gray-700 w-10">{f.priority}</span>
                        <button
                          onClick={() => priorityMutation.mutate({ id: f.id, priority: Math.max(1, f.priority - 1) })}
                          disabled={priorityMutation.isPending || f.priority <= 1}
                          aria-label={t('findings.priorityUp')}
                          className="px-2 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:border-gray-500 disabled:opacity-30"
                        >
                          ▲
                        </button>
                        <button
                          onClick={() => priorityMutation.mutate({ id: f.id, priority: Math.min(9999, f.priority + 1) })}
                          disabled={priorityMutation.isPending || f.priority >= 9999}
                          aria-label={t('findings.priorityDown')}
                          className="px-2 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:border-gray-500 disabled:opacity-30"
                        >
                          ▼
                        </button>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-gray-500 tabular-nums">{ageLabel(f.createdAtUtc, t)}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => exportFinding(f.id)}
                          className="px-2.5 py-1 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:border-gray-500"
                        >
                          {t('findings.export')}
                        </button>
                        <button
                          onClick={() => statusMutation.mutate({ id: f.id, next: 'Rejected' })}
                          disabled={terminal || statusMutation.isPending}
                          className="px-2.5 py-1 text-xs font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:border-red-400 disabled:opacity-30"
                        >
                          {t('findings.reject')}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {(findings ?? []).length === 0 && (
            <p className="text-sm text-gray-400 px-4 py-6 text-center">{t('findings.empty')}</p>
          )}
        </div>
      )}

      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}
